import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

const mockLoan = {
  id: "LN001",
  user: "Alice Johnson",
  amount: 1000,
  status: "Pending",
  disbursedAt: "2023-03-15",
  dueDate: "2024-03-15",
  interestRate: 5.5, 
  termMonths: 12,
  monthlyPayment: 85.61,
  description: "Personal loan for business expansion",
  paymentsMade: 3,
  paymentsRemaining: 9,
};

const mockPayments = [
  { id: "P001", dueDate: "2023-04-15", amount: 85.61, status: "Paid", paidAt: "2023-04-14" },
  { id: "P002", dueDate: "2023-05-15", amount: 85.61, status: "Paid", paidAt: "2023-05-15" },
  { id: "P003", dueDate: "2023-06-15", amount: 85.61, status: "Paid", paidAt: "2023-06-14" },
  { id: "P004", dueDate: "2023-07-15", amount: 85.61, status: "Due", paidAt: null },
  { id: "P005", dueDate: "2023-08-15", amount: 85.61, status: "Due", paidAt: null },
];

export default function LoanDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loan, setLoan] = useState(null);
  const [payments, setPayments] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState("");

  useEffect(() => {
    // TODO: fetch loan and payments from API using id
    setLoan(mockLoan);
    setPayments(mockPayments);
  }, [id]);

  if (!loan) {
    return <div className="p-6">Loading loan data...</div>;
  }

  const handleAction = (action) => {
    setActionLoading(true);
    setActionMessage("");

    setTimeout(() => {
      setActionLoading(false);
      setActionMessage(`Loan ${action} action completed.`);
      setLoan((prev) => ({
        ...prev,
        status:
          action === "approve"
            ? "Approved"
            : action === "reject"
            ? "Rejected"
            : prev.status,
      }));
    }, 1000);
  };

  return (
    <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow md:ml-64">
      <button
        onClick={() => navigate(-1)}
        className="mb-4 px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
      >
        &larr; Back to Loans
      </button>

      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white">
        Loan Details - {loan.id}
      </h2>

      {/* Admin Actions */}
      <div className="mb-6 space-x-3">
        {loan.status === "Pending" && (
          <>
            <button
              disabled={actionLoading}
              onClick={() => handleAction("approve")}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
            >
              Approve
            </button>
            <button
              disabled={actionLoading}
              onClick={() => handleAction("reject")}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
            >
              Reject
            </button>
          </>
        )}
        {loan.status === "Approved" && (
          <button
            disabled={actionLoading}
            onClick={() => handleAction("mark paid")}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            Mark Payment Received
          </button>
        )}
      </div>

      {actionMessage && (
        <div className="mb-6 text-green-700 dark:text-green-400 font-semibold">{actionMessage}</div>
      )}

      {/* Loan Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-gray-800 dark:text-gray-200 mb-8">
        <div>
          <p><span className="font-semibold">Loan ID:</span> {loan.id}</p>
          <p><span className="font-semibold">User:</span> {loan.user}</p>
          <p>
            <span className="font-semibold">Amount:</span>{" "}
            {loan.amount.toLocaleString(undefined, { style: "currency", currency: "CAD" })}
          </p>
          <p><span className="font-semibold">Status:</span> {loan.status}</p>
          <p><span className="font-semibold">Disbursed At:</span> {loan.disbursedAt || "-"}</p>
          <p><span className="font-semibold">Due Date:</span> {loan.dueDate || "-"}</p>
        </div>
        <div>
          <p><span className="font-semibold">Interest Rate:</span> {loan.interestRate}%</p>
          <p><span className="font-semibold">Term:</span> {loan.termMonths} months</p>
          <p>
            <span className="font-semibold">Monthly Payment:</span>{" "}
            {loan.monthlyPayment.toLocaleString(undefined, { style: "currency", currency: "CAD" })}
          </p>
          <p><span className="font-semibold">Payments Made:</span> {loan.paymentsMade}</p>
          <p><span className="font-semibold">Payments Remaining:</span> {loan.paymentsRemaining}</p>
          <p><span className="font-semibold">Description:</span> {loan.description}</p>
        </div>
      </div>

      {/* Payment Schedule Table */}
      <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Payment Schedule</h3>
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse border border-gray-200 dark:border-gray-700">
          <thead className="bg-gray-100 dark:bg-gray-700">
            <tr>
              <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Payment ID</th>
              <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Due Date</th>
              <th className="py-2 px-4 text-right text-sm font-semibold text-gray-700 dark:text-gray-300">Amount</th>
              <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Status</th>
              <th className="py-2 px-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Paid At</th>
            </tr>
          </thead>
          <tbody>
            {payments.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center py-6 text-gray-500 dark:text-gray-400">
                  No payments found.
                </td>
              </tr>
            ) : (
              payments.map((payment) => (
                <tr
                  key={payment.id}
                  className="border-t border-gray-200 dark:border-gray-700 hover:bg-blue-50 dark:hover:bg-gray-700"
                >
                  <td className="py-3 px-4">{payment.id}</td>
                  <td className="py-3 px-4">{payment.dueDate}</td>
                  <td className="py-3 px-4 text-right font-mono">
                    {payment.amount.toLocaleString(undefined, { style: "currency", currency: "CAD" })}
                  </td>
                  <td className="py-3 px-4">{payment.status}</td>
                  <td className="py-3 px-4">{payment.paidAt || "-"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
